import { useAppTheme } from '@droplet-tech-code/core-elements/module/theme';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { isNativeMobile } from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { SupportRequestListItemResponse } from '@naus-code/naus-admin-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useItemSeparatorComponent } from '~/components/FlatList/FlatList.Common';

import { useAppNavigation } from '~/hooks/nav.hook';
import { translate } from '~/utils/translation.utils';

import { supportRequestApi } from '../../SupportRequests.api';
import { VirtualInventoryRequestsListHeader } from './VirtualInventoryRequests.ListHeader';
import { FlatList } from '~/components/FlatList/FlatList';
import { SearchBar } from '@droplet-tech-code/core-elements/module/ui/Search/Search';
import { RefreshControl } from 'react-native-gesture-handler';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { useGetAuthUser } from '~/screens/Auth/Auth.Context';
import { SupportRequestListItemComponent } from '../../SupportRequest.ListItem';
import { PageHeader } from '~/components/Page/Page.Header';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';

export interface SupportRequestListProps {
  onPress: (item: SupportRequestListItemResponse) => void;
  id?: string;
}

export const SupportRequestListNative = () => {
  const navigate = useAppNavigation();
  return (
    <SupportRequestList
      onPress={(item) => {
        navigate.navigate('SupportRequest-Item', {
          id: item.id,
        });
      }}
    />
  );
};

export const SupportRequestList = (props: SupportRequestListProps) => {
  const selectedRef = useRef('');
  const { spacing } = useAppTheme();
  const { currentUser } = useGetAuthUser();
  const [stateData, setStateData] = useState<
    (SupportRequestListItemResponse & { type: string })[]
  >([]);

  const { data, isLoading, refetch, isFetching } =
    supportRequestApi.useGetVIRequestsOpenQuery(
      {
        adminUserId: currentUser?.uid || '',
        sortBy: 'dateOpenedSR',
      },
      {
        skip: !currentUser,
        pollingInterval: 2 * 60 * 1000,
      },
    );

  const currentSupportReqId = props.id || '';
  console.log('rerender', data);
  const renderItem = useCallback(
    (supportRequest: SupportRequestListItemResponse) => {
      return (
        <SupportRequestListItemComponent
          selected={currentSupportReqId === supportRequest.id}
          key={supportRequest.id}
          supportRequest={supportRequest}
          onPress={() => {
            selectedRef.current = supportRequest.id;
            props.onPress(supportRequest);
          }}
        />
      );
    },
    [currentSupportReqId],
  );

  const ItemSeparatorComponent = useItemSeparatorComponent();
  const searchData = [
    ...(data?.supportRequestsNeedAction.map((req) => ({ ...req, type: 'action' })) || []),
    ...(data?.supportRequestsWaiting.map((req) => ({ ...req, type: 'waiting' })) || []),
  ];

  useEffect(() => {
    setStateData(searchData);
  }, []);

  return (
    <VStack flex={1}>
      <VStack>
        <VirtualInventoryRequestsListHeader refetch={refetch} isLoading={isLoading} />
        <SearchBar
          inputContainerStyle={[{ backgroundColor: 'white' }]}
          data={searchData}
          deps={[searchData.length !== (stateData as any).length, isFetching]}
          onSearch={(filteredData) => {
            console.log({ filteredData });
            setStateData(filteredData);
          }}
        />
        <Divider />
      </VStack>
      <HStack flex={1} align="stretch">
        <VStack flex={1}>
          <ListHeader
            title={translate('supportRequests.virtualInventory.newVIBookings')}
            count={stateData.filter((req) => req.type === 'waiting').length}
          />
          <FlatList
            scrollEnabled
            refreshControl={
              <RefreshControl refreshing={isFetching} onRefresh={() => refetch()} />
            }
            contentContainerStyle={[
              {
                paddingTop: spacing[2.5],
              },

              isNativeMobile && { paddingHorizontal: spacing[2] },
            ]}
            feedback={translate('supportRequests.emptySupportRequest')}
            isLoading={isLoading}
            data={stateData.filter((req) => req.type === 'waiting')}
            simple
            renderItem={renderItem}
            onPressItem={(v) => {
              props.onPress(v);
            }}
            ItemSeparatorComponent={ItemSeparatorComponent}
          />
        </VStack>
        <Divider vertical />
        <VStack flex={1}>
          <ListHeader
            title={translate('supportRequests.virtualInventory.needAction')}
            count={stateData.filter((req) => req.type === 'action').length}
          />
          <FlatList
            refreshControl={
              <RefreshControl refreshing={isFetching} onRefresh={() => refetch()} />
            }
            contentContainerStyle={[
              {
                paddingTop: spacing[2.5],
              },

              isNativeMobile && { paddingHorizontal: spacing[2] },
            ]}
            feedback={translate('supportRequests.emptySupportRequest')}
            isLoading={isLoading}
            data={stateData.filter((req) => req.type === 'action')}
            simple
            renderItem={renderItem}
            onPressItem={(v) => {
              props.onPress(v);
            }}
            ItemSeparatorComponent={ItemSeparatorComponent}
          />
        </VStack>
      </HStack>
    </VStack>
  );
};

const ListHeader = ({ title, count }: { title: string; count: number }) => {
  return (
    <PageHeader
      title={title}
      Subtitle={
        <Text.Small>{`${count} ${translate('supportRequests.requests')}`}</Text.Small>
      }
    />
  );
};
